import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { D, designError, designErrorList, fmtDate, useDesignMe } from "@/lib/designApi";
import {
  DataTable, ErrorList, Field, HistoryPanel, ItemPicker, NoAccess, PageHeader, SimpleSelect, StatusBadge, WorkflowBar,
  useQueryId,
} from "@/components/design/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Search, Trash2, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

const newOp = (seq) => ({ seq, operation_code: "", description: "", workcenter_id: "", workcenter_name: "", setup_time_min: 0,
  cycle_time_min: 0, tooling: [], inspection_point: false, inspection_characteristics: [], work_instruction_doc_no: "", standard_refs: [] });

const cleanOps = (ops) => ops.map((o) => ({
  ...o, seq: Number(o.seq), setup_time_min: Number(o.setup_time_min) || 0, cycle_time_min: Number(o.cycle_time_min) || 0,
  inspection_characteristics: (o.inspection_characteristics || []).filter((c) => c.characteristic),
}));

function useWorkcenters() {
  const [wcs, setWcs] = useState([]);
  useEffect(() => { api.get("/workcenters").then((r) => setWcs(r.data || [])).catch(() => setWcs([])); }, []);
  return wcs;
}

function OpsEditor({ ops, setOps, disabled }) {
  const wcs = useWorkcenters();
  const upd = (i, k, v) => setOps((xs) => xs.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  const updChar = (i, ci, k, v) => setOps((xs) => xs.map((o, j) => (j !== i ? o : {
    ...o, inspection_characteristics: o.inspection_characteristics.map((c, cj) => (cj === ci ? { ...c, [k]: v } : c)),
  })));
  return (
    <div className="space-y-3" data-testid="routing-ops-editor">
      {ops.map((o, i) => (
        <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-white">
          <div className="grid grid-cols-12 gap-2">
            <Field label="Seq" className="col-span-1"><Input type="number" value={o.seq} disabled={disabled} onChange={(e) => upd(i, "seq", e.target.value)} /></Field>
            <Field label="Op code" className="col-span-2"><Input value={o.operation_code} disabled={disabled} onChange={(e) => upd(i, "operation_code", e.target.value)} /></Field>
            <Field label="Description *" className="col-span-4"><Input value={o.description} disabled={disabled} onChange={(e) => upd(i, "description", e.target.value)} data-testid={`op-desc-${i}`} /></Field>
            <Field label="Work centre" className="col-span-3">
              {wcs.length ? (
                <SimpleSelect value={o.workcenter_id} disabled={disabled} onChange={(v) => upd(i, "workcenter_id", v)}
                  options={wcs.map((w) => ({ value: w.id, label: `${w.code} — ${w.name}` }))} />
              ) : <Input value={o.workcenter_name} disabled={disabled} onChange={(e) => upd(i, "workcenter_name", e.target.value)} />}
            </Field>
            <Field label="Setup (min)" className="col-span-1"><Input type="number" min="0" value={o.setup_time_min} disabled={disabled} onChange={(e) => upd(i, "setup_time_min", e.target.value)} /></Field>
            <Field label="Cycle (min)" className="col-span-1"><Input type="number" min="0" step="any" value={o.cycle_time_min} disabled={disabled} onChange={(e) => upd(i, "cycle_time_min", e.target.value)} /></Field>
          </div>
          <div className="grid grid-cols-12 gap-2 items-end">
            <Field label="Tooling / fixtures (comma sep.)" className="col-span-5"><Input value={(o.tooling || []).join(", ")} disabled={disabled}
              onChange={(e) => upd(i, "tooling", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} /></Field>
            <Field label="Work instruction doc no." className="col-span-3"><Input value={o.work_instruction_doc_no} disabled={disabled} onChange={(e) => upd(i, "work_instruction_doc_no", e.target.value)} /></Field>
            <label className="col-span-3 text-sm flex items-center gap-2 pb-2"><Checkbox checked={!!o.inspection_point} disabled={disabled}
              onCheckedChange={(v) => setOps((xs) => xs.map((x, j) => (j === i ? { ...x, inspection_point: !!v, inspection_characteristics: v && !x.inspection_characteristics.length ? [{ characteristic: "", specification: "", method: "", instrument: "", frequency: "" }] : x.inspection_characteristics } : x)))} />
              <ClipboardCheck className="w-4 h-4 text-violet-600" />Inspection point</label>
            {!disabled && <button className="col-span-1 text-slate-400 hover:text-red-600 pb-2 justify-self-end" onClick={() => setOps((xs) => xs.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>}
          </div>
          {o.inspection_point && (
            <div className="bg-violet-50/50 rounded p-2 space-y-1">
              {o.inspection_characteristics.map((c, ci) => (
                <div key={ci} className="grid grid-cols-5 gap-2">
                  {["characteristic", "specification", "method", "instrument", "frequency"].map((k) => (
                    <Input key={k} placeholder={k} value={c[k] || ""} disabled={disabled} onChange={(e) => updChar(i, ci, k, e.target.value)} />
                  ))}
                </div>
              ))}
              {!disabled && <Button size="sm" variant="ghost" onClick={() => upd(i, "inspection_characteristics", [...o.inspection_characteristics, { characteristic: "", specification: "", method: "", instrument: "", frequency: "" }])}>+ characteristic</Button>}
            </div>
          )}
        </div>
      ))}
      {!disabled && <Button size="sm" variant="outline" onClick={() => setOps((xs) => [...xs, newOp((xs.length + 1) * 10)])} data-testid="routing-add-op"><Plus className="w-3.5 h-3.5 mr-1" />Add operation</Button>}
    </div>
  );
}

function RoutingDetail({ id, onChanged, onOpen }) {
  const { can } = useDesignMe();
  const [rec, setRec] = useState(null);
  const [ops, setOps] = useState([]);
  const [name, setName] = useState("");
  const [batch, setBatch] = useState(1);
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => {
    api.get(`${D}/routings/${id}`).then((r) => { setRec(r.data); setOps(r.data.operations || []); setName(r.data.name); setBatch(r.data.batch_qty); }).catch((e) => toast.error(designError(e)));
  }, [id]);
  useEffect(load, [load]);
  if (!rec) return null;
  const editable = rec.status === "draft" && can("create_edit");
  const total = ops.reduce((a, o) => a + (Number(o.setup_time_min) || 0) / (Number(batch) || 1) + (Number(o.cycle_time_min) || 0), 0);
  const save = async () => {
    setErrors([]);
    try {
      await api.patch(`${D}/routings/${id}`, { name, batch_qty: Number(batch) || 1, operations: cleanOps(ops) });
      toast.success("Saved");
      load();
      onChanged?.();
    } catch (e) {
      const l = designErrorList(e);
      setErrors(l.length ? l : [designError(e)]);
    }
  };
  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs text-slate-500 mono">{rec.number} · Rev {rec.revision}</div>
        <div className="text-xl font-bold text-slate-900">{rec.item_no} — {rec.item_name}</div>
      </div>
      <WorkflowBar entity="routings" record={rec} onChanged={(n) => (n?.id && n.id !== rec.id ? onOpen(n.id) : (load(), onChanged?.()))} />
      <Tabs defaultValue="ops">
        <TabsList><TabsTrigger value="ops">Operations ({ops.length})</TabsTrigger><TabsTrigger value="history">History</TabsTrigger></TabsList>
        <TabsContent value="ops" className="space-y-3">
          <div className="flex gap-3 items-end">
            <Field label="Routing name"><Input value={name} disabled={!editable} onChange={(e) => setName(e.target.value)} /></Field>
            <Field label="Batch qty (setup spread)"><Input type="number" min="1" value={batch} disabled={!editable} onChange={(e) => setBatch(e.target.value)} className="w-32" /></Field>
            <div className="text-sm text-slate-600 pb-2">Std time / unit: <b className="mono">{total.toFixed(2)} min</b></div>
          </div>
          <OpsEditor ops={ops} setOps={setOps} disabled={!editable} />
          <ErrorList errors={errors} />
          {editable && <div className="flex justify-end"><Button className="bg-blue-600 hover:bg-blue-700" onClick={save} data-testid="routing-save">Save draft</Button></div>}
        </TabsContent>
        <TabsContent value="history"><HistoryPanel entity="routings" record={rec} onOpenRevision={onOpen} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function DesignRoutings() {
  const { me, can, error: meErr } = useDesignMe();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [openId, setOpenId] = useState(useQueryId());
  const [creating, setCreating] = useState(false);
  const [itemNo, setItemNo] = useState("");
  const [ops, setOps] = useState([newOp(10)]);
  const [errors, setErrors] = useState([]);
  const load = useCallback(() => api.get(`${D}/routings`, { params: { q } }).then((r) => setRows(r.data)).catch(() => setRows([])), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("new")) { setCreating(true); setItemNo(p.get("item") || ""); }
  }, []);
  if (meErr || (me && !me.has_access)) return <NoAccess error={meErr} />;
  const create = async () => {
    setErrors([]);
    try {
      const { data } = await api.post(`${D}/routings`, { item_no: itemNo.trim().toUpperCase(), operations: cleanOps(ops.filter((o) => o.description)) });
      toast.success(`Created ${data.number}`);
      setCreating(false);
      setOps([newOp(10)]);
      load();
      setOpenId(data.id);
    } catch (e) {
      const l = designErrorList(e);
      setErrors(l.length ? l : [designError(e)]);
    }
  };
  return (
    <div className="space-y-6 max-md:space-y-3" data-testid="design-routings-page">
      <PageHeader title="Routings" subtitle="Operations, work centres, setup / cycle times, tooling and inspection points">
        {can("create_edit") && <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setCreating(true)} data-testid="design-new-routing"><Plus className="w-4 h-4 mr-1.5" />New routing</Button>}
      </PageHeader>
      <div className="relative w-72 max-md:w-full">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input className="pl-9" placeholder="Part no / name…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>
      <DataTable rows={rows} onRowClick={(r) => setOpenId(r.id)}
        columns={[
          { key: "number", title: "Routing", cellClass: "mono font-semibold" },
          { key: "revision", title: "Rev", cellClass: "mono" },
          { key: "item_no", title: "Part", cellClass: "mono" },
          { key: "item_name", title: "Name", cellClass: "max-w-xs truncate" },
          { key: "name", title: "Routing name" },
          { key: "status", title: "Status", render: (r) => <StatusBadge status={r.status} stage={r.review_stage} /> },
          { key: "updated_at", title: "Updated", render: (r) => fmtDate(r.updated_at) },
        ]} />
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New routing</DialogTitle></DialogHeader>
          <Field label="Part number *"><div className="w-72 max-md:w-full"><ItemPicker value={itemNo} onChange={setItemNo} /></div></Field>
          <OpsEditor ops={ops} setOps={setOps} />
          <ErrorList errors={errors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={!itemNo} onClick={create}>Create draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Sheet open={!!openId} onOpenChange={(o) => !o && setOpenId(null)}>
        <SheetContent side="right" className="w-full sm:max-w-6xl overflow-y-auto">
          <SheetHeader><SheetTitle>Routing</SheetTitle></SheetHeader>
          {openId && <RoutingDetail key={openId} id={openId} onChanged={load} onOpen={setOpenId} />}
        </SheetContent>
      </Sheet>
    </div>
  );
}
